import Alpine from "alpinejs";

/* global CKEDITOR */

import * as API from "./api";
import * as EmailScheduler from "./email_scheduler.js";
import * as RecipientPicker from "./recipient_picker.js";
import * as toast from "./components/toast.js";

import { initEmailCKEditors, initTagsList, EMAIL_TAGS_LIST_ID } from "../routes/user/email_templates/lib.js";

// Email validation regex - supports unicode letters in local part
const EMAIL_REGEX =
  /^[\p{L}0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/u;

const isValidEmail = (email) => EMAIL_REGEX.test(email);

const tryGetObjectFromDataSet = (form, key, defaultValue = undefined) => {
  try {
    let value = form.dataset[key];
    if (value && typeof value === "string") {
      value = JSON.parse(value);
    }
    return typeof value === "object" ? value : defaultValue;
  } catch (e) {
    console.error(e);
    console.error("The above error occurred because of", key, form.dataset[key]);
    return defaultValue;
  }
};

/**
 * Fetch templates (filtering by type) and tags in parallel
 */
async function fetchTemplatesAndTags(type, attachmentTagParam = {}) {
  const [templatesBody, tagsBody] = await Promise.all([
    API.fetchJSON(p`/ajax/user/email_templates/${type}?${attachmentTagParam}`),
    API.fetchJSON(p`/ajax/user/email_templates/${type}/tags?${attachmentTagParam}`),
  ]);

  return {
    modelTemplates: templatesBody.email_templates,
    tags: tagsBody.tags,
  };
}

export const openEmailCustomizer = (opts) => {
  return Alpine.$data(document.querySelector("#email-customizer")).openPopup(opts);
};

export const openEmailCustomizerFromForm = async (form, opts = {}) => {
  opts = { ...opts, attachmentTagParam: JSON.parse(opts.attachmentTagParam || "{}") };
  const recipientsLabel = form.dataset.recipientsLabel;
  const schedulable = form.dataset.schedulable === "true";
  const attachment = tryGetObjectFromDataSet(form, "attachment", null);
  const type = form.dataset.type;
  const availableRecipients = tryGetObjectFromDataSet(form, "availableRecipients", []).map((r, idx) => ({ ...r, idx }));
  const invalidRecipients = tryGetObjectFromDataSet(form, "invalidRecipients", []).map((r, idx) => ({ ...r, idx }));
  const defaultEmailTemplateId = form.dataset.defaultEmailTemplateId;

  // Only let manage CC if the target hidden input is there in the HTML (let the backend decide)
  const manageCc = form.querySelector("[name=cc]") !== null;

  const { recipients, cc, subject, body, template, scheduleAt } = await openEmailCustomizer({
    recipientsLabel,
    availableRecipients,
    invalidRecipients,
    attachment,
    schedulable,
    manageCc,
    type,
    defaultEmailTemplateId,
    ...opts,
  });

  if (manageCc) {
    form.querySelector("[name=cc]").value = JSON.stringify(cc);
  }
  form.querySelector("[name=recipients]").value = JSON.stringify(recipients);
  form.querySelector("[name=subject]").value = subject;
  form.querySelector("[name=body]").value = body;
  form.querySelector("[name=template_type]").value = template?.type || "";
  form.querySelector("[name=date]").value = scheduleAt?.toISOString() || "";
  form.setAttribute("action", form.dataset.action);

  // When hosted in a LiveView, submit via fetch to avoid a full page reload.
  // Mirror the native form submission (application/x-www-form-urlencoded, same
  // fields incl. _csrf_token) so the controller processes it identically.
  // Success looks different depending on the endpoint: the back-office one
  // answers with a 302 redirect, which redirect "manual" surfaces as an
  // opaqueredirect response, while the cockpit one answers 200 with JSON.
  if (form.dataset.noReload === "true") {
    try {
      const response = await API.fetch(form.dataset.action, {
        method: "POST",
        body: new URLSearchParams(new FormData(form)),
        redirect: "manual",
      });

      if (response.type === "opaqueredirect" || response.ok) {
        toast.show("#toast-template-success", form.dataset.successMessage);
      } else {
        console.error("Email send failed", response.status, response.statusText);
        toast.show("#toast-template-error");
      }
    } catch (e) {
      console.error("Email send failed", e);
      toast.show("#toast-template-error");
    }
    return;
  }

  form.submit();
};

// Open the email customizer when the button is clicked
// Each button carries the form that will be submitted by the generic modal
document.addEventListener("click", (event) => {
  if (!event.target.closest(".email-form button")) return;
  const form = event.target.closest("form");
  openEmailCustomizerFromForm(form, {
    attachmentTagParam: form.dataset.emailAttachmentTagParam,
  });
});

const getEditorData = (editor) => {
  const data = editor.getData();
  if (!data.trim()) {
    const wrapper = editor.container.$.closest(".text-input");
    wrapper.classList.add("text-input--error");
    setTimeout(() => {
      wrapper.classList.remove("text-input--error");
    }, 1000);
    return false;
  }
  return data;
};

Alpine.data("emailCustomizer", () => ({
  loaded: false,
  hasTemplateError: false,
  title: null,
  recipientsLabel: "",
  availableRecipients: [],
  invalidRecipients: [],
  attachment: null,
  modelTemplates: [],
  cc: [],

  // Internal state
  pickedRecipients: [],
  scheduleAt: null,
  selectedTemplateId: null,

  showTags: false,

  schedulable: false,
  testable: true,

  // Does the modal manages the recipients, or are they managed outside?
  manageRecipients: true,

  // Does the modal shows the CC input? This is false by default and driven by
  // the backend generated hidden input's presence.
  manageCc: false,

  resolve: null,

  async openPopup(opts) {
    this.hasTemplateError = false;
    this.loaded = false;
    this.title = opts.title;
    this.recipientsLabel = opts.recipientsLabel;
    this.availableRecipients = opts.availableRecipients ?? [];
    this.invalidRecipients = opts.invalidRecipients ?? [];
    this.schedulable = opts.schedulable ?? false;
    this.testable = opts.testable ?? true;
    this.manageRecipients = opts.manageRecipients ?? true;
    this.manageCc = opts.manageCc ?? false;
    this.attachment = opts.attachment ?? null;
    this.cc = opts.cc ?? [];
    this.pickedRecipients = this.availableRecipients.filter((recipient) => recipient.checked && !recipient.excluded);
    this.scheduleAt = null;

    try {
      const { modelTemplates, tags } = await fetchTemplatesAndTags(opts.type, opts.attachmentTagParam);
      this.modelTemplates = modelTemplates || [];
      window.replacements_tags = tags;
    } catch (_e) {
      this.hasTemplateError = true;
      this.modelTemplates = [];
      window.replacements_tags = [];
    }

    this.$popup.open(this.$root);

    await initEmailCKEditors();

    this.selectedTemplateId = parseInt(opts.defaultEmailTemplateId) || this.modelTemplates[0]?.id || null;

    this.setModelTemplate();

    initTagsList("", EMAIL_TAGS_LIST_ID);

    this.$root.addEventListener(
      "close",
      () => {
        for (const name in CKEDITOR.instances) {
          CKEDITOR.instances[name].destroy();
        }
      },
      { once: true }
    );

    // CKEditor take a bit of time and we only want to activate the submit
    // button once the email body is fully loaded and editable.
    // We arbitrarily wait for the biggest CKeditor on the modal
    CKEDITOR.instances["email_template_html"].on("instanceReady", () => {
      this.loaded = true;
    });

    return new Promise((resolve) => (this.resolve = resolve));
  },

  onSendEmail() {
    this.doSendEmail(false);
  },

  onSendTestEmail() {
    this.doSendEmail(true);
  },

  doSendEmail(isTest) {
    let recipients = this.pickedRecipients;
    const cc = this.cc;

    if (isTest) {
      const testRecipient = { ...this.availableRecipients[0], test: true };
      recipients = [testRecipient];
    }

    const subject = getEditorData(CKEDITOR.instances["email_template_subject"]);
    const body = getEditorData(CKEDITOR.instances["email_template_html"]);
    const ccInput = document.querySelector('input[name="cc"]:not([type="hidden"])');
    const ccInputValidy = ccInput ? ccInput.checkValidity() : true;

    if (!subject || !body || cc.length > 10 || !ccInputValidy) {
      if (!ccInputValidy) {
        ccInput.reportValidity();
      }
      return;
    }
    const template = this.modelTemplates.find((m) => m.id == this.selectedTemplateId);

    this.resolve({
      recipients,
      cc,
      subject,
      body,
      template: template,
      scheduleAt: this.scheduleAt,
    });

    this.$popup.close(this.$root);
  },

  onPickRecipients() {
    const recipients = this.availableRecipients.map((r) => ({
      ...r,
      customer_name: r.context.customer_name,
      company_name: r.context.company_name,
      company_id: r.context.company_id,
      checked: this.pickedRecipients.some((r2) => r2.idx === r.idx),
    }));

    RecipientPicker.pick({ recipients, invalidRecipients: this.invalidRecipients }).then((recipients) => {
      if (recipients !== null) {
        this.pickedRecipients = recipients;
      }
    });
  },

  onCcInput(e) {
    let cc;
    if (e.target.value.length > 0) {
      cc = e.target.value
        .split(",")
        .map((email) => email.trim())
        .filter((email) => email.length > 0);
    } else {
      cc = [];
    }
    const wrapper = e.target.closest(".text-input");
    const errorCount = document.querySelector(".js-cc-error-count");
    this.cc = cc;

    const hasInvalidEmail = cc.some((email) => !isValidEmail(email));
    const tooManyCc = cc.length > 10;

    if (tooManyCc) {
      errorCount.classList.remove("has-hidden");
      wrapper.classList.add("text-input--error");
    } else {
      errorCount.classList.add("has-hidden");
    }

    if (hasInvalidEmail) {
      const invalidEmailMessage = e.target.dataset.invalidEmailMessage || "Format d'email invalide";
      e.target.setCustomValidity(invalidEmailMessage);
      wrapper.classList.add("text-input--error");
    } else {
      e.target.setCustomValidity("");
    }

    if (!tooManyCc && !hasInvalidEmail) {
      wrapper.classList.remove("text-input--error");
    }

    if (hasInvalidEmail) {
      e.target.reportValidity();
    }
  },

  onModelTemplateChange() {
    // Unfortunately when setData is called, it clears the "paste" event which
    // breaks dropping tags into the editor. I (Antoine) spent a day trying to
    // workaround that and try to reattach the event, in vain.
    for (const name in CKEDITOR.instances) {
      CKEDITOR.instances[name].destroy();
    }
    initEmailCKEditors().then(() => {
      this.setModelTemplate();
    });
  },

  setModelTemplate() {
    const template = this.modelTemplates.find((m) => m.id == this.selectedTemplateId);
    CKEDITOR.instances["email_template_subject"].setData(template?.subject || "");
    CKEDITOR.instances["email_template_html"].setData(template?.html || "");
  },

  async onOpenScheduler() {
    const date = await EmailScheduler.getDate();
    this.scheduleAt = date;
    if (!this.scheduleAt) {
      return;
    }
    this.doSendEmail(false);
  },
}));
